import { useState, useMemo, useCallback } from "react";
import type { RowBase } from "../data-table.js";

type SortConfig<T> = {
  key: keyof T;
  direction: "asc" | "desc";
} | null;

export const useDataTableSorting = <TRow extends RowBase>(data: TRow[]) => {
  const [sortConfig, setSortConfig] = useState<SortConfig<TRow>>(null);

  const handleSort = useCallback((key: keyof TRow) => {
    setSortConfig((prev) => {
      if (prev?.key === key && prev.direction === "asc") {
        return { key, direction: "desc" };
      }
      return { key, direction: "asc" };
    });
  }, []);

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  return {
    sortConfig,
    handleSort,
    sortedData,
  };
};
